// ─────────────────────────────────────────────────────────────────────────────
// openclaw.config.ts — PRIMARY FORK CUSTOMIZATION FILE
//
// When forking this repo for a new sector or company, edit ONLY this file
// (plus public/favicon.svg and prisma/seed.ts) to rebrand the platform.
// All UI components, routes, and the Three.js animation consume values here.
// ─────────────────────────────────────────────────────────────────────────────

import type { OpenClawConfig } from './src/config/index'

const config: OpenClawConfig = {
  // ── Identity ────────────────────────────────────────────────
  app: {
    name: 'Bot Instance Provider',
    tagline: 'Franchise-First AI Agency Operations',
    description: 'Shared Ops managed platform for running white-label franchise AI workspaces across Telegram, WhatsApp, and phone.',
    logoUrl: '/favicon.svg',
  },

  // ── Sector ──────────────────────────────────────────────────
  // Used for sector-specific copy and agent metadata schemas.
  // Examples: 'general' | 'healthcare' | 'finance' | 'legal' | 'logistics'
  sector: 'general',
  sectorLabel: 'Franchise',

  // ── Model Providers available in this fork ──────────────────
  modelProviders: [
    { id: 'openai',    label: 'OpenAI',        models: ['gpt-4o', 'gpt-4o-mini', 'gpt-4-turbo'], apiKeyEnv: 'OPENAI_API_KEY' },
    { id: 'anthropic', label: 'Anthropic',      models: ['claude-opus-4-6', 'claude-sonnet-4-6', 'claude-haiku-4-5-20251001'], apiKeyEnv: 'ANTHROPIC_API_KEY' },
    { id: 'mistral',   label: 'Mistral AI',     models: ['mistral-large-latest', 'mistral-small-latest'], apiKeyEnv: 'MISTRAL_API_KEY' },
    { id: 'ollama',    label: 'Ollama (Local)', models: ['llama3', 'mistral', 'phi3'], apiKeyEnv: null },
  ],

  // ── Feature flags (disable per sector/plan) ─────────────────
  features: {
    agentCreation: true,
    chatInterface: true,
    usageAnalytics: true,
    multiOrg: false,           // Enable for enterprise forks
    apiKeyManagement: true,
    passkeys: true,            // Better Auth passkey-first login
  },

  // ── Theme tokens ─────────────────────────────────────────────
  // These map to CSS custom properties (--oc-*) in src/index.css.
  // Change these to rebrand colors without touching any component.
  theme: {
    accentColor: '#7c3aed',       // violet-600
    accentLight: '#a78bfa',       // violet-400
    accentDark: '#5b21b6',        // violet-800
    glassBlur: '12px',
    glassBg: 'rgba(255,255,255,0.06)',
    glassBorder: 'rgba(255,255,255,0.12)',
    glowColor: 'rgba(124,58,237,0.35)',
    background: '#05050f',
  },

  // ── Landing page copy ────────────────────────────────────────
  landing: {
    heroTitle: 'Run White-Label',
    heroTitleAccent: 'AI Franchises',
    heroSubtitle: 'Shared Ops controls hosting and credentials while each agency operates as a franchise workspace.',
    features: [
      {
        icon: 'Cpu',
        title: 'Shared Ops Control',
        description: 'Centralized hosting/provider management for all franchise workspaces.',
      },
      {
        icon: 'Shield',
        title: 'Curated Offerings',
        description: 'Audience and vertical template packs for every user group.',
      },
      {
        icon: 'BarChart3',
        title: 'Channel Runtime',
        description: 'Deploy Telegram, WhatsApp, and phone agents from one workspace.',
      },
      {
        icon: 'Zap',
        title: 'Billing Ready',
        description: 'Revenue-share usage metering and invoice-ready summaries.',
      },
    ],
    ctaText: 'Get Started',
    ctaSecondaryText: 'View Demo',
  },

  // ── Three.js animation ───────────────────────────────────────
  animation: {
    nodeCount: 90,
    connectionDistance: 150,
    particleColor: '#7c3aed',
    lineColor: '#7c3aed',
    mouseInfluenceRadius: 180,
    mouseInfluenceStrength: 0.4,
    meshWidth: 760,
    meshHeight: 440,
    meshSegmentsX: 72,
    meshSegmentsY: 42,
    meshAmplitude: 12,
    meshFrequency: 0.015,
    meshSpeed: 0.9,
    meshOpacity: 0.16,
    meshColor: '#a78bfa',
  },
}

export default config
